import apiClient from './api';
import { ApiResponse } from '../types';

export interface KnowledgeBase {
  id: string;
  organization_id: string;
  name: string;
  description: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  document_count?: number;
  source?: {
    type: string;  // 'manual' | 'woocommerce' | 'shopify' | 'custom_api'
    integration_id?: string;
    integration_name?: string;
  };
}

export interface KnowledgeDocument {
  id: string;
  knowledge_base_id: string;
  name: string;
  description: string;
  content: string;
  file_type: string;
  file_url?: string;
  file_size?: number;
  chunk_count?: number;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

class KnowledgeService {
  async getKnowledgeBases(): Promise<KnowledgeBase[]> {
    const response = await apiClient.get<ApiResponse<KnowledgeBase[]>>('/knowledge-bases');
    return response.data.data || [];
  }

  async getKnowledgeBase(id: string): Promise<KnowledgeBase> {
    const response = await apiClient.get<ApiResponse<KnowledgeBase>>(`/knowledge-bases/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to get knowledge base');
  }

  async createKnowledgeBase(data: { name: string; description?: string }): Promise<KnowledgeBase> {
    const response = await apiClient.post<ApiResponse<KnowledgeBase>>('/knowledge-bases', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create knowledge base');
  }

  async updateKnowledgeBase(id: string, data: Partial<KnowledgeBase>): Promise<KnowledgeBase> {
    const response = await apiClient.patch<ApiResponse<KnowledgeBase>>(`/knowledge-bases/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update knowledge base');
  }

  async deleteKnowledgeBase(id: string): Promise<void> {
    await apiClient.delete(`/knowledge-bases/${id}`);
  }

  async getDocuments(kbId: string): Promise<KnowledgeDocument[]> {
    const response = await apiClient.get<ApiResponse<KnowledgeDocument[]>>(`/knowledge-bases/${kbId}/documents`);
    return response.data.data || [];
  }

  async addDocument(kbId: string, data: { name: string; content: string; file_type?: string }): Promise<KnowledgeDocument> {
    const response = await apiClient.post<ApiResponse<KnowledgeDocument>>(`/knowledge-bases/${kbId}/documents`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to add document');
  }

  async uploadDocuments(kbId: string, files: File[]): Promise<{
    documents: Array<{ id: string; name: string; status: string }>;
    total_queued: number;
    rejected: string[];
  }> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await apiClient.post<ApiResponse<{
      documents: Array<{ id: string; name: string; status: string }>;
      total_queued: number;
      rejected: string[];
    }>>(`/knowledge-bases/${kbId}/documents/upload`, formData, {
      headers: {
        'Content-Type': undefined,
      },
      timeout: 60000,
    });

    if (response.data.data) return response.data.data;
    throw new Error('Failed to upload documents');
  }

  async deleteDocument(kbId: string, docId: string): Promise<void> {
    await apiClient.delete(`/knowledge-bases/${kbId}/documents/${docId}`);
  }

  async reprocessDocument(kbId: string, docId: string): Promise<void> {
    await apiClient.post(`/knowledge-bases/${kbId}/documents/${docId}/reprocess`);
  }

  getDocumentDownloadUrl(kbId: string, docId: string): string {
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}/knowledge-bases/${kbId}/documents/${docId}/download`;
  }

  async downloadDocument(kbId: string, docId: string, fileName: string): Promise<void> {
    const url = this.getDocumentDownloadUrl(kbId, docId);
    const token = localStorage.getItem('access_token');
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(blobUrl);
  }

  async search(query: string): Promise<KnowledgeDocument[]> {
    const response = await apiClient.post<ApiResponse<KnowledgeDocument[]>>('/knowledge-bases/search', { query });
    return response.data.data || [];
  }

  // Agent-specific KB operations

  async uploadFilesToAgent(agentId: string, files: File[]): Promise<{
    knowledge_base_id: string;
    documents: Array<{
      id: string;
      name: string;
      file_type: string;
      file_size: number;
      chunks_created: number;
      status: string;
    }>;
    total_uploaded: number;
  }> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await apiClient.post<ApiResponse<{
      knowledge_base_id: string;
      documents: Array<{
        id: string;
        name: string;
        file_type: string;
        file_size: number;
        chunks_created: number;
        status: string;
      }>;
      total_uploaded: number;
    }>>(`/agents/${agentId}/knowledge-bases/upload`, formData, {
      headers: {
        'Content-Type': undefined, // Let axios set multipart boundary automatically
      },
      timeout: 120000, // 2 minutes for large uploads
    });

    if (response.data.data) return response.data.data;
    throw new Error('Failed to upload files');
  }

  async getAgentDocuments(agentId: string): Promise<(KnowledgeDocument & { knowledge_base_name?: string })[]> {
    const response = await apiClient.get<ApiResponse<(KnowledgeDocument & { knowledge_base_name?: string })[]>>(
      `/agents/${agentId}/documents`
    );
    return response.data.data || [];
  }

  async deleteAgentDocument(agentId: string, docId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}/documents/${docId}`);
  }

  async getAgentKnowledgeBases(agentId: string): Promise<KnowledgeBase[]> {
    const response = await apiClient.get<ApiResponse<KnowledgeBase[]>>(`/agents/${agentId}/knowledge-bases`);
    return response.data.data || [];
  }

  async linkKnowledgeBaseToAgent(agentId: string, kbId: string): Promise<void> {
    await apiClient.post(`/agents/${agentId}/knowledge-bases`, { knowledge_base_id: kbId });
  }

  async unlinkKnowledgeBaseFromAgent(agentId: string, kbId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}/knowledge-bases/${kbId}`);
  }

  // Individual document linking — DEPRECATED
  // Whole KB linking is now the only supported approach.
}

export const knowledgeService = new KnowledgeService();
